<template lang="html">
  <div>
    <v-card flat>
      <v-card-title class="secondary white--text">
        <div class="ma-0">
          <div v-if="tableData" class="d-flex align-center">
            <v-icon large left color="white">mdi-home-account</v-icon>
            <p  class="subtitle-1 my-0">{{ tableData.Count || 0 }} Tenants <span v-if="isHistoric">| Tenant out of a tenancy with a balance</span>
            </p>
            <v-divider vertical class="mx-3 my-1" color="white"></v-divider>
            <span class="subtitle-1" v-if="showTotPayments">{{ totalPayments(this.tableData) }}</span>
          </div>
        </div>
        <v-spacer></v-spacer>
        <generic-search
          v-model="tenantId"
          label="Search Tenant"
          schema-type="LettingsTenant"
          :hide-details="true"
          :dark="true"
          @change="handleFilterChange"
        />
      </v-card-title>

      <!-- Table -->
      <v-data-table
        :headers="computedHeaders"
        :items="tableData && tableData.Data ? tableData.Data : []"
        :loading="isLoading"
        :search="search"
        :server-items-length="tableData && tableData.Count ? tableData.Count : 0"
        :options.sync="options"
        :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
        @update:options="handleOptionsUpdate"
        class="elevation-0"
        no-data-text="No tenants found"
        :items-per-page="10"
      >
        <template v-slot:item.TenantName="{ item }">
          <a class="accent--text" @click="openInfoDialog(item)">{{ item.TenantName }}</a>
        </template>
        
        <template v-slot:item.TenantAddress="{ item }">
          <span class="font-weight-light text-caption">{{ item.TenantAddress }}</span>
        </template>
        
        <template v-slot:item.TenantBalance="{ item }">
          <span>{{ item | returnDisplayTenantBalance }}</span>
        </template>
        
        <template v-slot:item.TenantInDebtDuration="{ item }" v-if="!isHistoric">
          <span class="font-weight-light text-caption">{{ item.TenantInDebtDuration }}</span>
        </template>
        
        <template v-slot:item.TenantIntoDebtDate="{ item }" v-if="!isHistoric">
          <span class="font-weight-light text-caption">{{ item.TenantIntoDebtDate | formatDate }}</span>
        </template>
        
        <template v-slot:item.actions="{ item }">
                  <v-menu offset-y left>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        color="secondary"
                        dark
                        small
                        depressed
                        v-bind="attrs"
                        v-on="on"
                        class="text-capitalize"
                      >
                        Actions
                        <v-icon right>mdi-chevron-down</v-icon>
                      </v-btn>
                    </template>
                    <v-list class="py-0 secondary" dark>
                      <v-list-item @click="fetchAccountSummary(item.TenantID)">
                        <v-list-item-title>View account summary</v-list-item-title>
                        <v-list-item-icon>
                          <v-icon small>mdi-open-in-new</v-icon>
                        </v-list-item-icon>
                      </v-list-item>
                      <v-list-item @click="applyCharge('Charge', item)">
                        <v-list-item-title>Apply Charge</v-list-item-title>
                        <v-list-item-icon>
                          <v-icon small>mdi-currency-gbp</v-icon>
                        </v-list-item-icon>
                      </v-list-item>
                      <v-list-item @click="applyCharge('Refund', item)">
                        <v-list-item-title>Apply Refund</v-list-item-title>
                        <v-list-item-icon>
                          <v-icon small>mdi-currency-gbp</v-icon>
                        </v-list-item-icon>
                      </v-list-item>
                      <v-list-item @click="navigateToPaymentRequests(item)">
                        <v-list-item-title>View payment requests</v-list-item-title>
                        <v-list-item-icon>
                          <v-icon small>mdi-folder-plus-outline</v-icon>
                        </v-list-item-icon>
                      </v-list-item>
                      <v-list-item @click="openDialog(item)">
                        <v-list-item-title>Contact support regarding this tenant</v-list-item-title>
                        <v-list-item-icon>
                          <v-icon small>mdi-comment-text-outline</v-icon>
                        </v-list-item-icon>
                      </v-list-item>
                      <v-list-item @click="requestSTO(item)">
                        <v-list-item-title>Request STO</v-list-item-title>
                        <v-list-item-icon>
                          <v-icon small>mdi-repeat</v-icon>
                        </v-list-item-icon>
                      </v-list-item>
                      <v-list-item @click="requestOneOffPayment(item)">
                        <v-list-item-title>Request One Off Payment</v-list-item-title>
                        <v-list-item-icon>
                          <v-icon small>mdi-currency-gbp</v-icon>
                        </v-list-item-icon>
                      </v-list-item>
                    </v-list>
                  </v-menu>
        </template>
      </v-data-table>
    </v-card>    

    <MsgDialog
      :dialog="dialog"
      :details="selectedItem.TenantName"
      :source="source"
      :OID="selectedItem.TenantID"
      @closeDialog="closeDialog"
      @msgSent="msgSent"
    />

    <Snackbar 
      :snackbarDialog="snackbar" 
      :snackbarText="snackbarText" 
      :snackbarColor="snackbarColor"
      @closeSnackbar="closeSnackbar()"
      :timeout="timeout"
    />

    <UserInfoDialog
      :dialog="infoDialog"
      :name="selectedItem.TenantName"
      :email="selectedItem.TenantEmail"
      :number="selectedItem.TenantMobile"
      :fullDetails="selectedItem"
      :selectedProperties="selectedProperties"
      :isLoading="infoLoading"
      @closeDialog="closeInfoDialog"
    />

    <v-dialog v-model="accountSummaryPopup" max-width="100%">
      <PersonAccountPopup
        :entries="accountSummary"
        @closeDialog="closeAccountSummaryPopup"
      />
    </v-dialog>
    <v-dialog v-model="applyChargePopup" max-width="500px">
    <ApplyChargeOrRefundForm
        :actionToApply="chargeAction"
        :personID="selectedItem.TenantID"
        @closeDialog="closeChargePopup"
      />
    </v-dialog>
    <v-dialog v-model="requestStoDialog" max-width="500px">
    <RequestPaymentDialog
        :selectedPaymentType="'Standing Order'"
        :personID="selectedItem.TenantID"
        @closeRequestPaymentDialog="closeRequestPaymentDialog"
      />
    </v-dialog>
    <v-dialog v-model="requestOneOffDialog" max-width="500px">
    <RequestPaymentDialog
        :selectedPaymentType="'One off payment'"
        :personID="selectedItem.TenantID"
        @closeRequestPaymentDialog="closeOneOffRequestPaymentDialog"
      />
    </v-dialog>
  </div>
</template>

<script>
import { amplifyProxy } from '@/services/directApi'
import MsgDialog from '@/components/Dialogs/MessageDialog'
import UserInfoDialog from '@/components/Dialogs/UserInfoDialog'
import Snackbar from '@/components/Snackbars/TheSnackbar'
import PersonAccountPopup from "@/components/BaseComponents/PersonAccountPopup.vue"
import ApplyChargeOrRefundForm from "../PaymentRequests/ApplyChargeOrRefundForm.vue"
import RequestPaymentDialog from '../PaymentRequests/PaymentRequestDialog.vue'
import GenericSearch from '@/components/CloudSearch/GenericSearch.vue'

export default {
  components: {
    MsgDialog,
    UserInfoDialog,
    Snackbar,
    PersonAccountPopup,
    ApplyChargeOrRefundForm,
    RequestPaymentDialog,
    GenericSearch
  },
  props: ['tableData', 'headers', 'showTotPayments', 'msg', 'isHistoric', 'isLoading'],
  data() {
    return {
      source: 'Tenants records',
      loading: false,
      search: '',
      selectedItem: '',
      dialog: false,
      infoDialog: false,
      msgText: '',
      snackbar: false,
      snackbarText: '',
      snackbarColor: '',
      timeout: 3000,
      loadingAccountsSummary: false,
      accountSummary: "",
      accountSummaryPopup: false,
      loadingChargePopup: false,
      chargeAction: "",
      applyChargePopup: false,
      requestStoDialog: false,
      requestOneOffDialog: false,
      selectedProperties: [],
      infoLoading: false,
      options: {
        page: 1,
        itemsPerPage: 10
      },
      // Filter data
      tenantId: ''
    }
  },
  computed: {
    hasActiveFilters() {
      return this.tenantId
    },
    computedHeaders() {
      const baseHeaders = [
        { text: 'Name', value: 'TenantName', sortable: false },
        { text: 'Address', value: 'TenantAddress', sortable: false },
        { text: 'Balance', value: 'TenantBalance', sortable: false }
      ]
      
      if (!this.isHistoric) {
        baseHeaders.push(
          { text: 'Days in Debt', value: 'TenantInDebtDuration', sortable: false },
          { text: 'Debt Date', value: 'TenantIntoDebtDate', sortable: false }
        )
      }
      
      baseHeaders.push({ text: 'Actions', value: 'actions', align: 'end', sortable: false })
      
      return baseHeaders
    }
  },
  methods: {
    closeSnackbar() {
      this.snackbar = false
    },

    openDialog(item) {
      this.selectedItem = item
      this.dialog = true
    },

    async openInfoDialog(item) {
      this.infoLoading = true

      try {
        const endpoint = `/${this.$store.state.user.ClientName}/${item.TenantID}/invoices/properties`
        const options = {
          headers: {
            token: this.$store.state.user.LoginToken
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        this.selectedProperties = response.data
        this.selectedItem = item
        this.infoDialog = true
      } catch (error) {
        console.error('Error fetching properties:', error)
      } finally {
        this.infoLoading = false
      }
    },

    closeDialog() {
      this.dialog = false
      this.msgText = ''
    },

    closeInfoDialog() {
      this.selectedProperties = []
      this.infoDialog = false
      this.selectedItem = ""
    },

    msgSent(payload) {
      this.dialog = false
      this.snackbar = true
      this.snackbarColor = payload.color
      this.snackbarText = payload.text
    },

    totalPayments(value) {
      const array = value.Data
      const payments = array.map(val => val.TenantBalance)
      const total = payments.reduce((acc, val) => acc + val, 0)
      return new Intl.NumberFormat('en-GB', { 
        style: 'currency', 
        currency: 'GBP' 
      }).format(total)
    },

    async fetchAccountSummary(id) {
      this.loadingPendingInvoice = true

      try {
        // Use the amplifyProxy wrapper which handles URL encoding for proper SigV4 signature
        const endpoint = `/${this.$store.state.user.ClientName}/company/person/${id}/account`
        const options = {
          headers: {
            token: this.$store.state.user.LoginToken
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        this.accountSummary = response.data
        this.accountSummaryPopup = true
      } catch (error) {
        console.error('Error fetching account summary:', error)
      } finally {
        this.loadingAccountsSummary = false
      }
    },

    closeAccountSummaryPopup() {
      this.accountSummary = ""
      this.accountSummaryPopup = false
    },

    applyCharge(actionToApply, item) {
      this.chargeAction = actionToApply
      this.selectedItem = item
      this.applyChargePopup = true
    },

    closeChargePopup() {
      this.chargeAction = ""
      this.applyChargePopup = false
    },

    requestSTO(item) {
      this.selectedItem = item
      this.requestStoDialog = true
    },

    requestOneOffPayment(item) {
      this.selectedItem = item
      this.requestOneOffDialog = true
    },

    closeRequestPaymentDialog() {
      this.requestStoDialog = false
    },

    closeOneOffRequestPaymentDialog() {
      this.requestOneOffDialog = false
    },


    navigateToPaymentRequests(item) {
      this.$router.push({ name: "Payments", params: { id: item.TenantID } })
    },
    
    handleOptionsUpdate(newOptions) {
      if (this.search || this.isLoading) return // Don't emit for client-side search or while loading
      
      let type = 'active';
      if (this.msg.includes('arrears')) type = 'arrears';
      else if (this.msg.includes('not chased')) type = 'notChased';
      else if (this.msg.includes('historic')) type = 'historic';
      
      this.$emit('paginate', type, newOptions.page, newOptions.itemsPerPage, { tenantId: this.tenantId });
    },
    handleFilterChange() {
      // Reset to first page when filters change
      this.options.page = 1;
      this.handleOptionsUpdate(this.options);
    },
    clearAllFilters() {
      this.tenantId = '';
      this.handleFilterChange();
    }
  },
  filters: {
    returnDisplayTenantBalance(val) {
      return val.TenantDisplayBalance
    }
  }
}
</script>

<style lang="css" scoped>
.position-relative {
  position: relative;
}
</style>